/**
 * @file RightsAnalysisSheet.tsx
 * @description 권리 분석 상세 리포트를 Sheet 컴포넌트로 표시하는 Client Component
 *
 * 주요 기능:
 * 1. Sheet 컴포넌트를 사용하여 권리 분석 상세 리포트 표시
 * 2. RightsAnalysisReport 컴포넌트를 Sheet 내부에 렌더링
 * 3. Premium 기능으로 제공 (Freemium 전략 반영 가능)
 *
 * 핵심 구현 로직:
 * - Client Component ('use client')
 * - Sheet 컴포넌트로 사이드 패널 표시
 * - SheetTrigger로 버튼 클릭 시 Sheet 열기
 * - RightsAnalysisReport에 rights와 courtDocs 전달
 *
 * @dependencies
 * - @/components/shared/ui/sheet: Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle
 * - @/components/simulations/reports/RightsAnalysisReport: RightsAnalysisReport 컴포넌트
 * - @/lib/types: Rights, CourtDocsNormalized 타입
 *
 * @see {@link /docs/refactoring/22-simulation-scope-matrix.md} - 현재 권리 리포트 유지 범위
 */

"use client";

import { useEffect, useState } from "react";
import {
  Rights,
  CourtDocsNormalized,
  Property,
  AllocationTable,
} from "@/lib/core/types";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/shared/ui/sheet";
import { X } from "lucide-react";
import { RightsAnalysisReport } from "@/components/simulations/reports/RightsAnalysisReport";
import { FileText } from "lucide-react";
import { Button } from "@/components/shared/ui/button";
import { Spinner } from "@/components/shared/ui/spinner";

export interface RightsAnalysisSheetProps {
  rights?: Rights;
  courtDocs?: CourtDocsNormalized;
  property?: Property;
  allocationTable?: AllocationTable;
  userBid?: number;
  simulationId?: string;
  caseId?: string;
  trigger?: React.ReactNode;
  /** 대시보드 히스토리 등, 이미 결제된 리포트를 여는 경우 true */
  isHistory?: boolean;
}

export function RightsAnalysisSheet({
  rights: initialRights,
  courtDocs: initialCourtDocs,
  property: initialProperty,
  allocationTable,
  userBid,
  simulationId,
  caseId,
  trigger,
  isHistory = false,
}: RightsAnalysisSheetProps) {
  const [open, setOpen] = useState(false);
  const [savedReport, setSavedReport] = useState<Record<string, unknown> | null>(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);

  const rights = (savedReport?.rights as Rights | undefined) || initialRights;
  const courtDocs =
    (savedReport?.courtDocs as CourtDocsNormalized | undefined) ||
    initialCourtDocs;
  const property =
    (savedReport?.property as Property | undefined) || initialProperty;

  useEffect(() => {
    if (!open || !isHistory) {
      return;
    }

    if (initialRights && initialCourtDocs && initialProperty) {
      return;
    }

    let ignore = false;

    async function loadSavedReport() {
      setIsLoadingReport(true);

      try {
        const response = await fetch("/api/auction/saved-reports?limit=50&offset=0");
        const data = await response.json();
        const rightsReport = data.items?.find(
          (item: { caseNo?: string; type?: string; result_snapshot?: Record<string, unknown> }) =>
            (item.caseNo === simulationId || item.caseNo === caseId) &&
            item.type === "rights_summary",
        );

        if (!ignore) {
          setSavedReport(rightsReport?.result_snapshot || null);
        }
      } catch {
        if (!ignore) {
          setSavedReport(null);
        }
      } finally {
        if (!ignore) {
          setIsLoadingReport(false);
        }
      }
    }

    loadSavedReport();

    return () => {
      ignore = true;
    };
  }, [
    open,
    isHistory,
    initialRights,
    initialCourtDocs,
    initialProperty,
    simulationId,
    caseId,
  ]);

  const defaultTrigger = (
    <Button
      variant="outline"
      className="flex w-full items-center gap-2 rounded-full border-sky-300 dark:border-sky-700 py-2 text-xs text-sky-700 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-900/30 hover:border-sky-400 dark:hover:border-sky-500 transition-all font-[var(--font-noto-sans-kr)]"
    >
      <FileText className="h-4 w-4 text-sky-700 dark:text-sky-400" />
      권리 분석 리포트 보기
    </Button>
  );

  return (
    <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          {trigger || defaultTrigger}
        </SheetTrigger>
        <SheetContent
        side="right"
        className="w-full sm:max-w-[210mm] flex flex-col overflow-hidden [&>button]:hidden [&>button]:lg:block"
      >
        <SheetTitle className="sr-only">권리 분석 리포트</SheetTitle>
        {/* 모바일 전용 닫기 버튼 */}
        <div className="lg:hidden relative z-10 flex items-center justify-end px-4 py-3 border-b border-border bg-background">
          <SheetClose asChild>
            <button
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-muted transition-colors flex-shrink-0"
              aria-label="닫기"
            >
              <X className="h-5 w-5 text-foreground" />
            </button>
          </SheetClose>
        </div>
        <div className="flex-1 overflow-y-auto min-h-0" data-lenis-prevent>
          {isLoadingReport ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center space-y-4">
                <Spinner className="w-8 h-8 mx-auto" />
                <p className="text-sm text-muted-foreground">저장된 리포트를 불러오는 중...</p>
              </div>
            </div>
          ) : rights && courtDocs && property ? (
            <RightsAnalysisReport
              rights={rights}
              courtDocs={courtDocs}
              property={property}
              allocationTable={allocationTable}
              userBid={userBid}
              isSample={false}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center space-y-4">
                <p className="text-sm text-muted-foreground">리포트 데이터를 불러올 수 없습니다.</p>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
